import React, { useState, useRef, useMemo, Fragment } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { NavLink, useParams, useHistory, Link } from 'react-router-dom'
import { Table, Modal, Tag, Input, Space, Button, Tooltip } from 'antd'
import { CarOutlined, PlusCircleOutlined, SearchOutlined, LinkOutlined } from '@ant-design/icons'
import { PageInner } from '../../components/shared/PageInner'
import ModalEditOrderFromAdmin from '../../components/ModalEditOrderFromAdmin/ModalEditOrderFromAdmin'
import ModalAddCommenInOrder from '../../components/ModalAddCommenInOrder/ModalAddCommenInOrder'
import PopoverComment from '../../components/PopoverComment/PopoverComment'
import SocialLogin from '../../components/SocialLogin/SocialLogin'
import AddOrderInfoFromManager from '../../components/AddOrderInfoFromManager/AddOrderInfoFromManager'
import { usePurchase } from './usePurchase'
import { useOrders } from './useOrders'
import ProfitSumProductsByManager from '../../components/ProfitSumProductsByManager/ProfitSumProductsByManager'
import { useTotalSumProfitByManager } from '../../hooks/useTotalSumProfitByManager'
import { useFormula } from '../../hooks/useFormula'

const TitleWrap = styled.div`
    text-align: center;
    margin-bottom: 15px;

    h2 {
        margin-bottom: 15px;
    }
`

const TableWrap = styled.div`
    max-width: 1920px;
    padding: 0 64px;
    margin: 0 auto;
    overflow-x: scroll;

    width: 100%;
    margin-top: 30px;

    table {
        .ant-table-cell {
            padding: 8px;
            white-space: nowrap;
        }

        .ant-table-row {
            &.packed {
                background-color: #fff9a861;
            }

            &.paidFor {
                background-color: #ffd7d0a7;
            }

            &.completed {
                background-color: #affaea4b;
            }
        }

        .ant-table-thead > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td,
        .ant-table-tbody > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td,
        .ant-table-thead > tr:hover:not(.ant-table-expanded-row) > td,
        .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td {
            background: #f5f1f1;
        }

        .ant-table-tbody > tr > td {
            border-bottom: 1px solid #cacaca;
        }

        .ant-table-filter-column {
            svg {
                color: inherit;
                width: 16px;
                height: 16px;
            }
        }

        .ant-tooltip {
            z-index: -1 !important;
        }
    }

    @media (max-width: 470px) {
        padding: 0 16px;
    }
`

const ProductItem = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;

    &[data-preQuantity='Y'] {
        background-color: pink;
    }

    .checkDefects {
        color: tomato;
        margin-left: 6px;
    }

    &.no {
        color: tomato;
    }

    &.yes {
        color: #33c733;
    }

    &.return {
        background-color: #000;
        color: #fff;
        border-radius: 2px;
        padding: 2px 4px;
    }

    &.yesMoscow {
        color: purple;
    }

    &.pending {
        background-color: #ffa99a;
        color: white;
        padding: 0 4px;
        border-radius: 2px;

        .checkDefects {
            color: white;
            margin-left: 6px;
        }

        .popoverPosition {
            svg {
                fill: white;
            }
        }
    }

    .linkIcon {
        margin-left: 6px;
    }
`

const AddProductLink = styled.div`
    display: flex;
    align-items: center;

    a {
        color: inherit;
        display: flex;
        align-items: center;

        svg {
            margin-right: 4px;
        }

        &:hover {
            color: #1890ff;
        }
    }
`

const AddTrackerWrap = styled.div`
    display: flex;
    align-items: center;

    span {
        &.delivery {
            margin-left: 6px;
        }
    }
`

const deliveryJSX = {
    belpochta: 'Белпочта',
    evropochta: 'Европочта',
    geoMinsk: 'Доставка'
}

function ManagerTableOrdersPage() {
    const dispatch = useDispatch()
    const token = useSelector((state) => state.token)
    const auth = useSelector((state) => state.auth)

    const params = useParams()
    const history = useHistory()

    const search = window.location.search
    const paramsAll = new URLSearchParams(search)
    const titlePurshuase = paramsAll.get('title')
    const articleOrder = paramsAll.get('articleOrder')

    const { isLoadingPurchase, purchase, getPurchase } = usePurchase(token, params.id)
    const { isLoadingOrders, orders, getOrders } = useOrders(params.id, token, auth.user._id)
    const totalSumProfit = useTotalSumProfitByManager(orders, auth.user)

    const [isModalVisible, setIsModalVisible] = useState(false)

    const [searchText, setSearchText] = useState('')
    const [searchedColumn, setSearchedColumn] = useState('')
    const searchInput = useRef(null)

    const [page, setPage] = useState(1)
    const [backPage, setBackPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const filteredOrders = orders?.filter((item) => item.isManager)
    const searchItem = filteredOrders?.filter((item) => item.articleOrder === +articleOrder)

    const arr = searchItem ? searchItem : []
    const dataTable = articleOrder ? arr : filteredOrders

    function handleSearch(selectedKeys, confirm, dataIndex) {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
    }

    function handleReset(clearFilters) {
        clearFilters()
        setSearchText('')
    }

    const onShowSizeChange = (current, size) => {
        setPageSize(size)
    }

    const columns = useMemo(
        () => [
            {
                title: 'Номер',
                dataIndex: 'articleOrder',
                width: 70,

                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInput}
                            placeholder={`Поиск по логину`}
                            value={selectedKeys[0]}
                            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={() => handleSearch(selectedKeys, confirm, 'userLogin')}
                            style={{ width: 188, marginBottom: 8, display: 'block' }}
                        />

                        <Space>
                            <Button
                                type='primary'
                                onClick={() => handleSearch(selectedKeys, confirm, 'userLogin')}
                                icon={<SearchOutlined />}
                                size='small'
                                style={{ width: 90 }}
                            >
                                Поиск
                            </Button>

                            <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
                                Сброс
                            </Button>
                        </Space>
                    </div>
                ),

                filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,

                onFilter: (value, { author, info, userLogin }) =>
                    (info?.instaLogin && info?.instaLogin.toString().toLowerCase().includes(value.toLowerCase())) ||
                    (userLogin && userLogin.toString().toLowerCase().includes(value.toLowerCase())),

                onFilterDropdownVisibleChange: (visible) => {
                    if (visible) {
                        setTimeout(() => searchInput.current.select())
                    }
                },

                render: (text, record, index) => {
                    return (
                        <>
                            {record.articleOrder}

                            {record.personalPercentAdmdin && (
                                <div>
                                    <Tag color='purple'>Скидка: {record.personalPercentAdmdin}</Tag>
                                </div>
                            )}

                            <SocialLogin record={record} />
                        </>
                    )
                }
            },

            {
                title: 'ФИО',
                dataIndex: 'userName',
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInput}
                            placeholder={`Поиск по тел и ФИО`}
                            value={selectedKeys[0]}
                            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={() => handleSearch(selectedKeys, confirm, 'userName')}
                            style={{ width: 188, marginBottom: 8, display: 'block' }}
                        />

                        <Space>
                            <Button
                                type='primary'
                                onClick={() => handleSearch(selectedKeys, confirm, 'userName')}
                                icon={<SearchOutlined />}
                                size='small'
                                style={{ width: 90 }}
                            >
                                Поиск
                            </Button>

                            <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
                                Сброс
                            </Button>
                        </Space>
                    </div>
                ),

                filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,

                onFilter: (value, record) =>
                    record.info.phone.toString().toLowerCase().includes(value.toLowerCase()) ||
                    record.info.userName.toString().toLowerCase().includes(value.toLowerCase()),

                onFilterDropdownVisibleChange: (visible) => {
                    if (visible) {
                        setTimeout(() => searchInput.current.select())
                    }
                },

                render: (text, record, index) => {
                    return (
                        <>
                            {(record.isAdmin || record?.isManager) && (
                                <AddProductLink>
                                    <NavLink to={`/purchase/${params.id}/add-product/${record._id}`}>
                                        <PlusCircleOutlined />
                                    </NavLink>

                                    <ModalAddCommenInOrder order={record} getOrders={getOrders} />
                                    <ModalEditOrderFromAdmin order={record} getOrders={getOrders} />
                                </AddProductLink>
                            )}

                            <AddProductLink>
                                {record.info.userName.length >= 15 ? (
                                    <Tooltip title={`${record.info.userName}`}>
                                        <span>
                                            {record.info.userName.length >= 15 ? (
                                                <Link
                                                    to={`/manager/products-by-order/${params.id}/${record._id}?articleOrder=${record.articleOrder}`}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    {`${record.info.userName.substr(0, 15)}...`}
                                                </Link>
                                            ) : (
                                                <Link
                                                    to={`/manager/products-by-order/${params.id}/${record._id}?articleOrder=${record.articleOrder}`}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                >
                                                    {record.info.userName}
                                                </Link>
                                            )}
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Link
                                        to={`/manager/products-by-order/${params.id}/${record._id}?articleOrder=${record.articleOrder}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        {record.info.userName}
                                    </Link>
                                )}
                            </AddProductLink>

                            <span>{record.info.phone}</span>

                            <br />

                            {record?.info.address && record.info.address.length >= 15 ? (
                                <Tooltip title={`${record.info.address}`}>
                                    <span>
                                        {record.info.address.length >= 15
                                            ? `${record.info.address.substr(0, 15)}...`
                                            : record.info.address}
                                    </span>
                                </Tooltip>
                            ) : (
                                record.info.address
                            )}
                        </>
                    )
                }
            },

            {
                title: 'Доставка',
                dataIndex: 'info',
                render: (text, record, index) => {
                    return (
                        <>
                            <AddTrackerWrap>
                                <CarOutlined /> <span className='delivery'>{deliveryJSX[record.info.delivery]}</span>
                            </AddTrackerWrap>

                            {record?.trackerPochta && <div>Трек:</div>}
                            {record?.trackerPochta && <div>{record.trackerPochta}</div>}
                        </>
                    )
                }
            },

            {
                title: '№',
                dataIndex: 'productNumber',
                width: 70,

                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInput}
                            placeholder={`Поиск по логину`}
                            value={selectedKeys[0]}
                            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={() => handleSearch(selectedKeys, confirm, 'userLogin')}
                            style={{ width: 188, marginBottom: 8, display: 'block' }}
                        />

                        <Space>
                            <Button
                                type='primary'
                                onClick={() => handleSearch(selectedKeys, confirm, 'userLogin')}
                                icon={<SearchOutlined />}
                                size='small'
                                style={{ width: 90 }}
                            >
                                Поиск
                            </Button>

                            <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
                                Сброс
                            </Button>
                        </Space>
                    </div>
                ),

                filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,

                onFilter: (value, { author, info, products }) => {
                    const product = products && products.find((item) => item.productNumber === +value)
                    return (
                        product &&
                        product.productNumber &&
                        product.productNumber.toString().toLowerCase().includes(value.toLowerCase())
                    )
                },

                onFilterDropdownVisibleChange: (visible) => {
                    if (visible) {
                        setTimeout(() => searchInput.current.select())
                    }
                },

                render: (text, record, index) => {
                    return record.products.map((item, productIndex) => (
                        <p key={`productNumber_${productIndex}`}>{item.productNumber}</p>
                    ))
                }
            },

            {
                title: 'Продукты',
                dataIndex: 'products',
                render: (text, record, index) => {
                    return text.map((item) => (
                        <ProductItem key={item._id} className={item.status}>
                            {item.nameProduct.length >= 15 ? (
                                <Tooltip title={`${item.nameProduct}`}>
                                    <span>
                                        {item.nameProduct.length >= 15
                                            ? `${item.nameProduct.substr(0, 15)}...`
                                            : item.nameProduct}
                                    </span>
                                </Tooltip>
                            ) : (
                                item.nameProduct
                            )}

                            {item.checkDefects && <b className='checkDefects'>(Б)</b>}

                            {item.commentAdmin && (
                                <PopoverComment comment={item.commentAdmin} className='popoverPosition' />
                            )}

                            {item.productLink ? (
                                <a href={`${item.productLink}`} target='_blank' rel='noopener noreferrer'>
                                    <LinkOutlined className='linkIcon' />
                                </a>
                            ) : null}
                        </ProductItem>
                    ))
                }
            },

            {
                title: 'Цвет',
                dataIndex: 'products',
                render: (text, record, index) => {
                    return text.map((item) => (
                        <Fragment key={item._id}>
                            {item.color.length >= 10 ? (
                                <Tooltip title={`${item.color}`}>
                                    <span>
                                        {item.color.length >= 10 ? (
                                            <ProductItem key={item._id}>{`${item.color.substr(0, 10)}...`}</ProductItem>
                                        ) : (
                                            <ProductItem key={item._id}>{item.color}</ProductItem>
                                        )}
                                    </span>
                                </Tooltip>
                            ) : (
                                <ProductItem>{item.color}</ProductItem>
                            )}
                        </Fragment>
                    ))
                }
            },

            {
                title: 'Размер',
                dataIndex: 'products',
                render: (text, record, index) => {
                    return text.map((item) =>
                        item.size.length >= 8 ? (
                            <Tooltip title={`${item.size}`} key={item._id}>
                                <span>
                                    {item.size.length >= 8 ? (
                                        <ProductItem key={item._id}>{`${item.size.substr(0, 8)}...`}</ProductItem>
                                    ) : (
                                        <ProductItem key={item._id}>{item.size}</ProductItem>
                                    )}
                                </span>
                            </Tooltip>
                        ) : (
                            <ProductItem key={item._id}>{item.size}</ProductItem>
                        )
                    )
                }
            },

            {
                title: 'Кол-во',
                dataIndex: 'products',
                render: (text, record, index) => {
                    return text.map((item) => (
                        <ProductItem key={item._id} data-preQuantity={item.preQuantity ? 'Y' : 'N'}>
                            {!item.preQuantity ? `${item.quantity}` : `${item.preQuantity} из ${item.quantity}`}
                        </ProductItem>
                    ))
                }
            },

            {
                title: 'Цена рос.руб',
                dataIndex: 'products',
                // width: 100,
                render: (text, record, index) => {
                    return text.map((item) => <ProductItem key={item._id}>{item.price}</ProductItem>)
                }
            },

            {
                title: 'Цена про-жи бел.руб',
                dataIndex: 'products',
                // width: 100,
                render: (text, record, index) => {
                    return text.map((item) => <ProductItem key={item._id}>{item.managerPrice}</ProductItem>)
                }
            },

            {
                title: 'Зар-ок',
                dataIndex: 'products',
                // width: 100,
                render: (text, record, index) => {
                    if (text.length !== 0) {
                        return <ProfitSumProductsByManager user={auth.user} products={text} />
                    }
                    return 0
                }
            }
        ],
        [params.id, searchText, searchedColumn, purchase]
    )

    const onChangeHandle = (pagination, filters, sorter, extra) => {
        if (extra.action === 'paginate') {
            setPage(pagination.current)
        }
        return
    }

    const showModal = () => {
        setIsModalVisible((prev) => !prev)
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    if (isLoadingPurchase || isLoadingOrders) {
        return <div className='container'>Loading...</div>
    }

    return (
        <section>
            <PageInner>
                <TitleWrap>
                    <h2>Итоговая таблица по закупке - {titlePurshuase && titlePurshuase}:</h2>
                    <Space direction='vertical'>
                        {purchase?.[0]?.status && (
                            <Button variant='secondary' onClick={showModal}>
                                Добавить заказ
                            </Button>
                        )}
                        {totalSumProfit && <div>Общий заработок за закупку = {totalSumProfit}</div>}
                    </Space>

                    {articleOrder && (
                        <Button
                            variant='secondary'
                            onClick={() => {
                                history.push({
                                    pathname: `/manager/table-orders/${purchase[0]._id}?title=${titlePurshuase}`,
                                    search: ''
                                })
                            }}
                        >
                            Сбросить
                        </Button>
                    )}
                </TitleWrap>

                <TableWrap>
                    <Table
                        columns={columns}
                        dataSource={dataTable}
                        onChange={onChangeHandle}
                        rowKey='_id'
                        rowClassName={(record) => record?.status && `${record?.status}`}
                        pagination={{
                            pageSize: pageSize,
                            total: filteredOrders.length,
                            current: page,
                            onShowSizeChange: onShowSizeChange
                        }}
                    />
                </TableWrap>
            </PageInner>

            <Modal
                title='Оформление заказа от имени manager:'
                visible={isModalVisible}
                footer={null}
                onCancel={handleCancel}
            >
                <AddOrderInfoFromManager
                    paramId={params.id}
                    orders={filteredOrders}
                    showModal={showModal}
                    titlePurchase={titlePurshuase}
                    getOrders={getOrders}
                    isManager
                />
            </Modal>
        </section>
    )
}

export default ManagerTableOrdersPage
